import { ChangeDetectionStrategy, Component } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { LicenseService } from '../../../shared/license-list/services/license.service';
import { LocalSettingsService } from '../../../../lib/services/local-settings.service';
import { NihiiHelper } from '../../../shared/helpers/nihii.helper';

@Component({
  selector: 'ms-ms-light-activation-dialog',
  templateUrl: './ms-light-activation-dialog.component.html',
  styleUrls: ['./ms-light-activation-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class MsLightActivationDialogComponent {
  public readonly form: FormGroup<{
    encodedNihii: FormControl<string>;
  }> = new FormGroup({
    encodedNihii: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required, this.validateNihii],
    }),
  });

  constructor(
    private dialogRef: MatDialogRef<MsLightActivationDialogComponent>,
    private licenseService: LicenseService,
    private localSettingsService: LocalSettingsService,
  ) {}

  public get encodedNihiiControl(): FormControl<string> {
    return this.form.controls.encodedNihii;
  }

  public get normalizedNihii(): string {
    return NihiiHelper.normalizeToNihii8(this.encodedNihiiControl.value);
  }

  public get hasNihiiError(): boolean {
    return this.encodedNihiiControl.invalid && (this.encodedNihiiControl.dirty || this.encodedNihiiControl.touched);
  }

  public get canSubmit(): boolean {
    return this.form.valid;
  }

  public closeDialog(): void {
    this.dialogRef.close();
  }

  public addLicense(): void {
    this.form.markAllAsTouched();

    if (!this.form.valid) {
      return;
    }

    void this.submitLicense();
  }

  private async submitLicense(): Promise<void> {
    const token: string | null = this.localSettingsService.oauthIdToken;
    if (!token) {
      return;
    }

    await this.licenseService.createOption(
      token,
      this.normalizedNihii,
      'MS_LIGHT',
    );

    this.dialogRef.close({ created: true });
  }

  private validateNihii(control: AbstractControl<string | null>): ValidationErrors | null {
    const normalizedNihii: string = NihiiHelper.normalizeToNihii8(control.value ?? '');

    if (!normalizedNihii) {
      return null;
    }

    if (normalizedNihii.length < 8) {
      return { nihiiLength: true };
    }

    if (!NihiiHelper.hasValidCheckDigit(normalizedNihii)) {
      return { nihiiCheckDigit: true };
    }

    return null;
  }
}
